import { useStore } from './useStore'
import { useProductStore } from '~/stores/product'
import type { Store } from '~/types/store'
import type {
  StockObject,
  StoreAvailabilityInfo,
  EcomAvailabilityInfo,
  StoreAvailabilityInfoSummary,
  StoreStockStatus,
  ProductAlgolia
} from '~/types/product'

export function useStock() {
  const { stores } = useStore()
  const productStore = useProductStore()

  //
  // ECOMMERCE STOCK
  //
  const getEcomAvailabilityInfo = (stock: StockObject, erpinfo: ProductAlgolia['erpinfo']): EcomAvailabilityInfo => {
    if (!stock || Array.isArray(stock) || !stock.stockStatus) {
      return defaultEcomStock
    }

    return {
      stockStatus: stock.stockStatus,
      availabilityText: getEcomAvailabilityText(stock),
      deliveryPhrase: getDeliveryEstimationPhrase(stock, erpinfo),
      qty: stock.qty || 0,
      cssClasses: getStatusCssClasses(stock.stockStatus),
      icon: getStatusIcon(stock.stockStatus)
    }
  }

  //
  // PHYSICAL STORE STOCK
  //
  const getStoreAvailabilityInfo = (stockObject: StockObject): StoreAvailabilityInfo => {
    if (!stockObject || Array.isArray(stockObject) || !stockObject.stockStatus) {
      return getDefaultStoreStock(stores)
    }

    return stores.reduce((availabilities, store): StoreAvailabilityInfo => ({
      ...availabilities,
      [store.code]: {
        onDisplay: !!stockObject.storeAvailability?.[store.code]?.onDisplayStock || false,
        storeStock: stockObject.storeAvailability?.[store.code]?.storeStock || 0,
        stockStatus: getStoreStatus(stockObject, store.code),
        availabilityText: getStoreAvailabilityText(stockObject, store.code)
      }
    }), {})
  }

  //
  // Physical store stock summary for the current product
  //
  const getStoreAvailabilityInfoSummary = computed(() => {
    const defaultSummary: StoreAvailabilityInfoSummary = {
      ecomStockStatus: 'outOfStock',
      onDisplayStoreCodes: [],
      inStockStoreCodes: [],
      variationsOnDisplayStoreCodes: []
    }
    const currentProduct: ProductAlgolia | null = productStore.currentProduct
    if (!currentProduct) return defaultSummary

    const stock: StockObject = { ...currentProduct['stock'] }
    defaultSummary.ecomStockStatus = stock?.stockStatus

    if (stock === undefined || stock === null || Array.isArray(stock) || !stock.stockStatus) {
      return defaultSummary
    }

    const summary = stores.reduce((summary, shop): StoreAvailabilityInfoSummary => {
      if (stock.storeAvailability?.[shop.code]?.storeStock) {
        summary.inStockStoreCodes.push(shop.code)
      }
      if (stock.storeAvailability?.[shop.code]?.onDisplayStock) {
        summary.onDisplayStoreCodes.push(shop.code)
      }
      if (productStore.variationsOnDisplay(shop.code).filter((p: ProductAlgolia) => p.sku !== currentProduct.sku).length > 0) {
        summary.variationsOnDisplayStoreCodes.push(shop.code)
      }
      return summary
    }, defaultSummary)

    return summary
  })

  //
  // Physical store availability text for the current product
  //
  const storeAvailabilityText = computed(() => {
    const { ecomStockStatus, inStockStoreCodes, onDisplayStoreCodes, variationsOnDisplayStoreCodes } = getStoreAvailabilityInfoSummary.value

    const codesToString = (codes: string[], from = false): string => {
      if (codes.length === 1) {
        const storeGenetive = stores.find(store => store.code === codes[0])?.label.genitive
        if (storeGenetive) {
          return storeGenetive
        }
      }
      const _in = ['yhdessä', 'kahdessa', 'kolmessa', 'neljässä', 'viidessä', 'kuudessa']
      return from ? _in[codes.length - 1].replace('ss', 'st') : _in[codes.length - 1]
    }

    return inStockStoreCodes.length
      ? `Katso myymäläsaatavuus`
      : onDisplayStoreCodes.length
        ? `Esillä ${codesToString(onDisplayStoreCodes)} myymälässä`
        : variationsOnDisplayStoreCodes.length
          ? `Vastaava malli esillä ${codesToString(variationsOnDisplayStoreCodes)} myymälässä`
          : ecomStockStatus !== 'outOfStock' ? 'Tilattavissa myymälästä' : 'Ei saatavilla myymälästä'
  })

  return {
    getEcomAvailabilityInfo,
    getStoreAvailabilityInfo,
    // getStoreAvailabilityInfoSummary,
    storeAvailabilityText
  }
}

const isAvailableInAnyStoreStock = (stock: StockObject): boolean => {
  if (!stock || !stock.storeAvailability) return false
  return Object.values(stock.storeAvailability).some(store => store.storeStock > 0)
}

/*
 * Returns stock quantity as a string: '3 kpl'
 * When limit is exceeded, returns 'yli {limit} kpl'
 */
const getQtyEstimate = (qty: number, limit: number): string => (qty <= limit) ? `${qty} kpl` : `yli ${limit} kpl`

/*
* Returns stock quantity as a string: '3 kpl'
* When limit is exceeded, returns 'yli {limit} kpl'
*/
const getStoreAvailabilityText = (stock: StockObject, storeCode: string): string => {
  if (!stock || !stock.storeAvailability) return 'Loppu varastosta'

  switch (getStoreStatus(stock, storeCode)) {
    case 'inStock':
      return `Varastossa ${getQtyEstimate(stock.storeAvailability?.[storeCode]?.storeStock || 0, 20)}`
    case 'backorder':
      return 'Tilattavissa'
    default:
      return 'Loppu varastosta'
  }
}

/*
* Returns stock status for a store: 'inStock'|'backorder'|'outOfStock'
*/
const getStoreStatus = (stock: StockObject, storeCode: string): StoreStockStatus => {
  if (!stock || !stock.storeAvailability || !stock.storeAvailability[storeCode]) return 'outOfStock'

  return stock.storeAvailability[storeCode].storeStock > 0
    ? 'inStock'
    : stock.stockStatus !== 'outOfStock' ? 'backorder' : 'outOfStock'
}

/*
* Returns availability text for e-commerce stock, e.g. 'Varastossa 3 kpl', 'Tulossa varastoon', 'Tilapäisesti loppu'
*/
const getEcomAvailabilityText = (stock: StockObject): string => {
  if (!stock || !stock.stockStatus) return 'Loppu varastosta'

  switch (stock.stockStatus) {
    case 'inStock':
      return `Varastossa ${getQtyEstimate(stock?.qty || 0, 10)}`
    case 'backorder':
      return 'Tilattavissa'
    default:
      return 'Loppu varastosta'
  }
}

/*
* Returns delivery estimation for a product, e.g. 'Toimitusaika 2-5 arkipäivää', 'Lähetettävissä n. 15.3.2021', 'Vain myymälästä'
*/
const getDeliveryEstimationPhrase = (stock: StockObject, erpinfo: ProductAlgolia['erpinfo']): string => {
  if (!stock || !stock.stockStatus) return ''

  switch (stock.stockStatus) {
    case 'inStock':
      return erpinfo?.productDeliverTimeInStock?.value || 'Toimitusaika 2-7 arkipäivää'
    case 'backorder':
      return erpinfo?.productDeliverTimeOutOfStock?.value || 'Toimitusaika 2-4 viikkoa'
    case 'outOfStock':
      return isAvailableInAnyStoreStock(stock) ? 'Vain myymälästä' : ''
    default:
      return ''
  }
}

const getStatusCssClasses = (stockStatus: StockObject['stockStatus']): EcomAvailabilityInfo['cssClasses'] => {
  if (!stockStatus) return 'text-red-700'

  switch (stockStatus) {
    case 'inStock':
      return 'text-green-500'
    case 'backorder':
      return 'text-yellow-400'
    default:
      return 'text-red-700'
  }
}

const getStatusIcon = (stockStatus: string): EcomAvailabilityInfo['icon'] => {
  if (!stockStatus) return 'x-circle-icon'

  switch (stockStatus) {
    case 'inStock':
      return 'check-circle-icon'
    case 'backorder':
      return 'clock-icon'
    default:
      return 'x-circle-icon'
  }
}

const defaultEcomStock: EcomAvailabilityInfo = {
  stockStatus: 'outOfStock',
  availabilityText: 'Loppu varastosta',
  deliveryPhrase: '',
  cssClasses: 'text-red-700',
  icon: 'x-circle-icon',
  qty: 0
}

const getDefaultStoreStock = (stores: Store[]): StoreAvailabilityInfo => {
  return stores.reduce((availabilities, store): StoreAvailabilityInfo => ({
    ...availabilities,
    [store.code]: {
      status: 'outOfStock',
      availabilityText: 'Loppu varastosta',
      onDisplay: false,
      storeStock: 0
    }
  }), {})
}
